import { createContext, type FC, type ReactElement, useContext, useState } from 'react';
import { getUserInfo, type ResponseAWSUserParams, type UserModel } from '../models';
import { extractClientId, isAdminRole } from '../utils';

interface IAuthContext {
  authUpdate: boolean;
  toggleAuthUpdate: () => void;
  awsAuth: ResponseAWSUserParams | undefined;
  setAwsAuth: (val: ResponseAWSUserParams | undefined) => void;
  auth: UserModel | undefined;
  setAuth: (val: UserModel | undefined) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  refreshUserInfo: () => void;
  getHomePage: () => string | null;
}

export const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: FC<{ children: ReactElement[] }> = ({ children }) => {
  const [authUpdate, setAuthUpdate] = useState(false);
  const [auth, setAuth] = useState<UserModel | undefined>(undefined);
  const [awsAuth, setAwsAuth] = useState<ResponseAWSUserParams | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const toggleUpdate = (): void => setAuthUpdate(!authUpdate);
  const refreshUserInfo = async (): Promise<void> => {
    const userInfo = await getUserInfo(auth?.id as string);
    setAuth(userInfo);
  };
  const getHomePage = (): string | null => {
    if (!auth) return null;
    if (isAdminRole(auth)) {
      return '/visits/list';
    } else {
      const clientId = extractClientId(auth);
      if (clientId) {
        return `/clients/${clientId}/dashboard`;
      } else {
        return '/visits/list';
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authUpdate,
        toggleAuthUpdate: toggleUpdate,
        refreshUserInfo,
        auth,
        setAuth,
        loading,
        awsAuth,
        setAwsAuth,
        setLoading,
        getHomePage
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext muse be used within a AuthContextProvider');
  }
  return context;
}
